/**
 * Детерміновані м'які (pastel, low-saturation) кольори для колонок Kanban —
 * стиль Linear / Notion, без неону. Один і той самий seed завжди дає один колір.
 *
 * Відомі статуси (active/SUCCEEDED/PENDING/...) мапляться на ті ж відтінки, що й
 * `StatusBadge.svelte`, щоб дошка і список були візуально узгоджені.
 *
 * Усі значення повертаються як готові CSS-кольори (hsl), бо динамічні Tailwind-
 * класи (`bg-emerald-100`) не можна згенерувати на рантаймі при JIT.
 */

export interface ColumnAccent {
  /** Колір крапки/акценту заголовка. */
  dot: string;
  /** Світла заливка тіла колонки. */
  bg: string;
  /** Колір рамки. */
  border: string;
  /** Колір тексту бейджа/лічильника. */
  text: string;
}

/** Фіксований відтінок (hue) для відомих семантичних значень. */
const SEMANTIC_HUE: Record<string, number | 'gray'> = {
  // позитив / успіх
  active: 152,
  succeeded: 152,
  paid: 152,
  done: 152,
  true: 152,
  yes: 152,
  так: 152,
  // помилка / негатив
  inactive: 4,
  failed: 4,
  refunded: 4,
  error: 4,
  false: 4,
  no: 4,
  ні: 4,
  // очікування
  pending: 38,
  processing: 38,
  waiting: 38,
  // нейтраль
  none: 'gray',
  unknown: 'gray',
  '': 'gray',
};

/** Стабільний хеш рядка → невід'ємне ціле (djb2). */
function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Палітра відтінків, рознесених по колу так, щоб сусідні значення відрізнялись. */
const HUE_WHEEL = [262, 210, 152, 38, 4, 320, 280, 178, 96, 22, 340, 230];

function accentFromHue(hue: number): ColumnAccent {
  return {
    dot: `hsl(${hue} 55% 56%)`,
    bg: `hsl(${hue} 60% 97%)`,
    border: `hsl(${hue} 45% 89%)`,
    text: `hsl(${hue} 42% 38%)`,
  };
}

const GRAY: ColumnAccent = {
  dot: 'hsl(250 8% 62%)',
  bg: 'hsl(250 16% 98%)',
  border: 'hsl(250 14% 90%)',
  text: 'hsl(250 8% 45%)',
};

/**
 * Колір колонки за її значенням (опція dropdown, статус, країна, тощо).
 * `null`/порожнє → сірий. Відомі статуси → семантичний відтінок.
 */
export function columnAccent(value: string | null | undefined): ColumnAccent {
  if (value == null || value === '') return GRAY;
  const key = String(value).trim().toLowerCase();
  const sem = SEMANTIC_HUE[key];
  if (sem === 'gray') return GRAY;
  if (typeof sem === 'number') return accentFromHue(sem);
  // згенерувати з палітри за хешем — стабільно і без кислотних кольорів
  const hue = HUE_WHEEL[hashString(key) % HUE_WHEEL.length];
  return accentFromHue(hue);
}
